import { useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'

import { AlertTriangleIcon } from 'lucide-react'

import { usePlans } from '@/api/queries'
import { PageBody, PageHeader } from '@/components/AppShell'
import { ErrorState, TableSkeleton } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { staffSubscriptionLive } from '@/lib/billing'
import { formatAbsolute, formatMicros } from '@/lib/format'
import { centsLabel, safeDocumentUrl } from '@/lib/billing'

import {
  adjustBalance,
  changePlan,
  overrideQuotas,
  runAction,
  usePoliceFunding,
  useStaffAccount,
  useStaffMutation,
  type AccountDetail,
  type BalanceKind,
  type FundingLot,
  type RefundRow,
  type Limits,
  type StaffAction,
} from '../api'
import { parseAmount, parseQuota } from '../forms'
import { useStaff } from '../hooks'
import { beginViewAs } from '../session'
import { ActionNotice, Fact, ReasonDialog, StaffGate, StaffLink, StateBadge } from '../ui'
import { CreditNoteLink, RefundResultPanel, RefundStateBadge, ResolveRefundDialog } from '../refunds'
import { SELECT_CLASS } from '../shared'
import { StaffInvoicesCard } from '../invoices'

type Dialog = 'suspend' | 'reactivate' | 'plan' | 'quotas' | 'balance' | 'view-as'

const QUOTA_FIELDS: { key: keyof Limits; label: string }[] = [
  { key: 'max_zones', label: 'Zones' },
  { key: 'max_connections_per_zone', label: 'Connections per zone' },
  { key: 'max_channels_per_zone', label: 'Channels per zone' },
  { key: 'max_members', label: 'Members' },
]

/** stripe.md §10: the subscription, the seats and the police auto-recharge settings, read-only. */
function SubscriptionCard({ account }: { account: AccountDetail }) {
  const sub = account.subscription
  const recharge = account.police_recharge
  return (
    <Card data-testid="staff-subscription">
      <CardHeader>
        <CardTitle>Subscription and police recharge</CardTitle>
        <CardDescription>Read-only. A live Stripe subscription is changed by the customer in the console.</CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
          <Fact label="Source">{sub?.source ?? 'none'}</Fact>
          <Fact label="Status">{sub?.status ?? 'none'}{staffSubscriptionLive(sub) ? <> <Badge variant="default">live</Badge></> : null}</Fact>
          <Fact label="Interval">{sub?.interval ?? '—'}</Fact>
          <Fact label="Extra seats"><span className="tabular-nums">{sub?.extra_seats ?? 0}</span></Fact>
          <Fact label="Period ends">{sub?.current_period_end ? formatAbsolute(sub.current_period_end) : '—'}</Fact>
          <Fact label="Cancels at period end">{sub?.cancel_at_period_end ? 'yes' : 'no'}</Fact>
          <Fact label="Over limit since">{sub?.over_limit_since ? formatAbsolute(sub.over_limit_since) : '—'}</Fact>
          <Fact label="Stripe customer"><span className="font-mono text-xs break-all">{sub?.stripe_customer_id ?? '—'}</span></Fact>
          <Fact label="Auto-recharge">{recharge?.enabled ? 'on' : 'off'}{recharge?.card_saved ? ' · card saved' : ' · no card'}</Fact>
          <Fact label="Recharge threshold">{recharge ? formatMicros(recharge.threshold_micros) : '—'}</Fact>
          <Fact label="Recharge amount">{recharge ? formatMicros(recharge.amount_micros) : '—'}</Fact>
        </dl>
      </CardContent>
    </Card>
  )
}

/** 2026-10-TOPUP-TAX: the police money behind the balance: lots, what is refundable, refunds, requests. */
function PoliceFundingCard({ accountId, canWrite, onResolve }: { accountId: string; canWrite: boolean; onResolve: (r: RefundRow) => void }) {
  const funding = usePoliceFunding(accountId)
  const f = funding.data
  return (
    <Card data-testid="staff-police-funding">
      <CardHeader>
        <CardTitle>Police funding</CardTitle>
        <CardDescription>
          Each paid top-up is a lot, consumed oldest first and refunded newest first. Goodwill credit is
          consumed last and is never refundable.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {funding.isError ? (
          <ErrorState error={funding.error} title="Could not load the police funding" onRetry={() => void funding.refetch()} />
        ) : f === undefined ? (
          <TableSkeleton rows={2} columns={4} />
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
              <Fact label="Balance"><span className="tabular-nums">{formatMicros(f.balance_micros)}</span></Fact>
              <Fact label="Refundable now"><span className="tabular-nums" data-testid="staff-refundable">{formatMicros(f.refundable_micros)}</span></Fact>
              <Fact label="Held by requests"><span className="tabular-nums">{formatMicros(f.reserved_by_requests_micros)}</span></Fact>
              <Fact label="Reserved in flight"><span className="tabular-nums">{formatMicros(f.reserved_micros)}</span></Fact>
              <Fact label="Lot debt"><span className="tabular-nums">{formatMicros(f.lot_debt_micros)}</span></Fact>
            </dl>

            <div className="overflow-x-auto" data-testid="staff-lots">
              <p className="mb-2 text-sm font-medium">Lots ({f.lots.length})</p>
              {f.lots.length === 0 ? (
                <p className="text-sm text-muted-foreground">No top-ups yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Created</TableHead>
                      <TableHead>Kind</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead className="text-right">Credited</TableHead>
                      <TableHead className="text-right">Tax paid</TableHead>
                      <TableHead className="text-right">Consumed</TableHead>
                      <TableHead className="text-right">Refunded</TableHead>
                      <TableHead className="text-right">Remainder</TableHead>
                      <TableHead>Invoice</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {f.lots.map((l) => <LotRow key={l.id} lot={l} />)}
                  </TableBody>
                </Table>
              )}
            </div>

            <div className="overflow-x-auto" data-testid="staff-refunds">
              <p className="mb-2 text-sm font-medium">Refunds ({f.refunds.length})</p>
              {f.refunds.length === 0 ? (
                <p className="text-sm text-muted-foreground">None.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Card total</TableHead>
                      <TableHead>Credit note</TableHead>
                      {canWrite ? <TableHead /> : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {f.refunds.map((r) => (
                      <TableRow key={r.id} data-testid="staff-refund-row" data-state={r.state}>
                        <TableCell className="text-xs text-muted-foreground">{formatAbsolute(r.created_at)}</TableCell>
                        <TableCell><Badge variant="outline">{r.source.toLowerCase()}</Badge></TableCell>
                        <TableCell><RefundStateBadge state={r.state} />{r.failure_code === null ? null : <span className="block text-xs text-muted-foreground">{r.failure_code}</span>}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatMicros(r.amount_micros)}</TableCell>
                        <TableCell className="text-right tabular-nums">{centsLabel(r.tax_cents)}</TableCell>
                        <TableCell className="text-right tabular-nums">{centsLabel(r.total_cents)}</TableCell>
                        <TableCell className="max-w-[14rem]"><CreditNoteLink row={r} /></TableCell>
                        {canWrite ? (
                          <TableCell>
                            {r.state === 'PENDING_MANUAL' ? (
                              <Button size="sm" variant="outline" onClick={() => { onResolve(r) }}>Resolve</Button>
                            ) : null}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {f.requests.length === 0 ? null : (
              <div data-testid="staff-account-requests">
                <p className="mb-2 text-sm font-medium">Refund requests ({f.requests.length})</p>
                <ul className="space-y-1 text-sm">
                  {f.requests.map((q) => (
                    <li key={q.id} className="flex flex-wrap items-center gap-2">
                      <span className="tabular-nums">{formatMicros(q.amount_micros)}</span>
                      <Badge variant="secondary">{q.state.toLowerCase().replace('_', ' ')}</Badge>
                      <span className="text-xs text-muted-foreground">{formatAbsolute(q.created_at)}</span>
                      {q.state === 'REQUESTED' ? <StaffLink to="/app/staff/refunds">review in the queue</StaffLink> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function LotRow({ lot: l }: { lot: FundingLot }) {
  const invoice = safeDocumentUrl(l.invoice_url)
  return (
    <TableRow data-testid="staff-lot-row" data-kind={l.kind}>
      <TableCell className="text-xs text-muted-foreground">{formatAbsolute(l.created_at)}</TableCell>
      <TableCell><Badge variant={l.kind === 'PAID' ? 'default' : 'secondary'}>{l.kind.toLowerCase()}</Badge></TableCell>
      <TableCell><StateBadge state={l.state} /></TableCell>
      <TableCell className="text-right tabular-nums">{formatMicros(l.amount_micros)}</TableCell>
      <TableCell className="text-right tabular-nums">{centsLabel(l.tax_cents)}</TableCell>
      <TableCell className="text-right tabular-nums">{formatMicros(l.consumed_micros)}</TableCell>
      <TableCell className="text-right tabular-nums">{formatMicros(l.refunded_micros + l.refund_pending_micros)}</TableCell>
      <TableCell className="text-right tabular-nums">{formatMicros(l.remainder_micros)}{l.refundable ? '' : l.kind === 'GOODWILL' ? ' · never' : ''}</TableCell>
      <TableCell className="max-w-[12rem] truncate font-mono text-xs">
        {l.invoice_id === null ? '—' : invoice === null ? l.invoice_id : (
          <a href={invoice} target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-2 hover:underline">{l.invoice_id}</a>
        )}
      </TableCell>
    </TableRow>
  )
}

function PlanDialog({ account, onClose, onDone }: { account: AccountDetail; onClose: () => void; onDone: (n: string, notified?: boolean) => void }) {
  const plans = usePlans()
  const live = staffSubscriptionLive(account.subscription)
  const [plan, setPlan] = useState(account.plan)
  const mutation = useStaffMutation(({ reason }: { reason: string }) => changePlan(account.id, plan, reason))
  return (
    <ReasonDialog
      title="Change plan"
      description={<>Moves <strong>{account.name}</strong> to another plan. Their administrators are emailed with your reason.</>}
      confirmLabel="Change plan"
      extraValid={plan !== account.plan && !live}
      onClose={onClose}
      onConfirm={async (reason) => {
        const r = await mutation.mutateAsync({ reason })
        onDone(`Plan changed to ${plan}`, r.notified)
      }}
    >
      {live ? (
        <Alert variant="warning" data-testid="subscription-live-warning">
          <AlertTriangleIcon />
          <AlertTitle>This account has a live Stripe subscription</AlertTitle>
          <AlertDescription>
            A staff plan change is refused while one exists. Change the plan in Stripe, or cancel the
            subscription first: two sources must never fight over one account.
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="space-y-1.5">
        <Label htmlFor="plan-select">Plan</Label>
        <select id="plan-select" className={SELECT_CLASS} value={plan} onChange={(e) => { setPlan(e.target.value) }}>
          {(plans.data ?? [{ code: account.plan, name: account.plan_name }]).map((p) => (
            <option key={p.code} value={p.code}>{p.name}</option>
          ))}
        </select>
      </div>
    </ReasonDialog>
  )
}

function QuotaDialog({ account, onClose, onDone }: { account: AccountDetail; onClose: () => void; onDone: (n: string, notified?: boolean) => void }) {
  const [values, setValues] = useState<Record<keyof Limits, string>>(() => ({
    max_zones: account.overrides.max_zones?.toString() ?? '',
    max_connections_per_zone: account.overrides.max_connections_per_zone?.toString() ?? '',
    max_channels_per_zone: account.overrides.max_channels_per_zone?.toString() ?? '',
    max_members: account.overrides.max_members?.toString() ?? '',
  }))
  const parsed = QUOTA_FIELDS.map((f) => parseQuota(values[f.key]))
  const valid = parsed.every((p) => p.ok)
  const mutation = useStaffMutation(({ reason }: { reason: string }) => {
    const pick = (i: number) => {
      const p = parsed[i]
      return p?.ok ? p.value : null
    }
    return overrideQuotas(account.id, {
      max_zones: pick(0),
      max_connections_per_zone: pick(1),
      max_channels_per_zone: pick(2),
      max_members: pick(3),
    }, reason)
  })
  return (
    <ReasonDialog
      title="Override quotas"
      description="A blank field returns that limit to the plan’s. Every field is replaced on save. Their administrators are emailed with your reason."
      confirmLabel="Save overrides"
      extraValid={valid}
      onClose={onClose}
      onConfirm={async (reason) => {
        const r = await mutation.mutateAsync({ reason })
        onDone('Quota overrides saved', r.notified)
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {QUOTA_FIELDS.map((f, i) => (
          <div key={f.key} className="min-w-0 space-y-1.5">
            <Label htmlFor={`q-${f.key}`}>{f.label}</Label>
            <Input
              id={`q-${f.key}`}
              inputMode="numeric"
              placeholder={`plan: ${account.limits[f.key]}`}
              value={values[f.key]}
              aria-invalid={!(parsed[i]?.ok ?? true)}
              onChange={(e) => { setValues((v) => ({ ...v, [f.key]: e.target.value })) }}
            />
          </div>
        ))}
      </div>
    </ReasonDialog>
  )
}

function BalanceDialog({ account, onClose, onDone }: { account: AccountDetail; onClose: () => void; onDone: (n: string, notified?: boolean, detail?: ReactNode) => void }) {
  const [kind, setKind] = useState<BalanceKind>('credit')
  const [amount, setAmount] = useState('')
  const parsed = parseAmount(amount)
  const mutation = useStaffMutation(({ reason }: { reason: string }) => adjustBalance(account.id, kind, parsed ?? '0', reason))
  return (
    <ReasonDialog
      title="Credit or refund"
      description={<>
        {kind === 'credit'
          ? <>Adds a goodwill ledger entry to <strong>{account.name}</strong>’s balance ({account.balance.currency}); it is never edited in place, and goodwill is never refundable.</>
          : <>Refunds <strong>{account.name}</strong> to their card from unspent paid top-ups, newest first: a Stripe credit note per top-up, tax included. It cannot exceed what is refundable.</>}
        {' '}A superoperator’s single entry is capped by the server. Their administrators are emailed with your reason.
      </>}
      confirmLabel={kind === 'credit' ? 'Add credit' : 'Add refund'}
      extraValid={parsed !== null}
      onClose={onClose}
      onConfirm={async (reason) => {
        const r = await mutation.mutateAsync({ reason })
        onDone(
          kind === 'credit' ? 'Credit added' : r.refund?.pending_manual ? 'Refund issued, card payout pending' : 'Refund issued',
          r.notified,
          r.refund ? <RefundResultPanel result={r.refund} /> : undefined,
        )
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="bal-kind">Kind</Label>
          <select id="bal-kind" className={SELECT_CLASS} value={kind} onChange={(e) => { setKind(e.target.value as BalanceKind) }}>
            <option value="credit">Credit</option>
            <option value="refund">Refund</option>
          </select>
        </div>
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="bal-amount">Amount ({account.balance.currency})</Label>
          <Input id="bal-amount" inputMode="decimal" placeholder="25.00" value={amount} aria-invalid={amount !== '' && parsed === null} onChange={(e) => { setAmount(e.target.value) }} />
        </div>
      </div>
    </ReasonDialog>
  )
}

export function StaffAccountPage() {
  const { accountId } = useParams()
  const staff = useStaff()
  const client = useQueryClient()
  const navigate = useNavigate()
  const account = useStaffAccount(accountId)
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [notice, setNotice] = useState<{ title: string; notified: boolean | undefined; detail?: ReactNode } | null>(null)
  const [resolving, setResolving] = useState<RefundRow | null>(null)
  const act = useStaffMutation(({ action, reason }: { action: StaffAction; reason: string }) => runAction(action, reason))
  const done = (title: string, notified?: boolean, detail?: ReactNode) => { setNotice({ title, notified, detail }) }
  const data = account.data
  const suspended = data?.account_state !== 'ACTIVE'

  return (
    <StaffGate>
      <PageHeader
        eyebrow="Staff · Account"
        title={data?.name ?? 'Account'}
        description={data === undefined ? undefined : <span className="font-mono text-xs">{data.id}</span>}
        actions={
          data === undefined ? undefined : (
            <div className="flex flex-wrap justify-end gap-2">
              {staff.can('impersonation') ? <Button variant="outline" onClick={() => { setDialog('view-as') }}>View as</Button> : null}
              {staff.can('billing_plan_quota') ? (
                <>
                  <Button variant="outline" onClick={() => { setDialog('plan') }}>Change plan</Button>
                  <Button variant="outline" onClick={() => { setDialog('quotas') }}>Override quotas</Button>
                  <Button variant="outline" onClick={() => { setDialog('balance') }}>Credit / refund</Button>
                </>
              ) : null}
              {staff.can('suspend_reactivate') ? (
                suspended ? (
                  <Button onClick={() => { setDialog('reactivate') }}>Reactivate</Button>
                ) : (
                  <Button variant="destructive" onClick={() => { setDialog('suspend') }}>Suspend</Button>
                )
              ) : null}
            </div>
          )
        }
      />
      <PageBody>
        <ActionNotice notice={notice} onDismiss={() => { setNotice(null) }} />
        {account.isError ? (
          <ErrorState error={account.error} title="Could not load this account" onRetry={() => void account.refetch()} />
        ) : data === undefined ? (
          <TableSkeleton rows={4} columns={3} />
        ) : (
          <>
            <Card>
              <CardContent>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
                  <Fact label="State"><StateBadge state={data.account_state} /></Fact>
                  <Fact label="Plan">{data.plan_name} <span className="font-mono text-xs text-muted-foreground">({data.plan})</span></Fact>
                  <Fact label="Balance">{formatMicros(data.balance.balance_micros, data.balance.currency)}</Fact>
                  <Fact label="Pending deletions">{data.pending_deletion_count}</Fact>
                  <Fact label="Created">{formatAbsolute(data.created_at)}</Fact>
                  <Fact label="Last activity">{data.last_activity_at === null ? '—' : formatAbsolute(data.last_activity_at)}</Fact>
                  <Fact label="State changed">{data.state_changed_at === null ? '—' : formatAbsolute(data.state_changed_at)}</Fact>
                  <Fact label="Slug"><span className="font-mono text-xs">{data.slug}</span></Fact>
                </dl>
              </CardContent>
            </Card>

            <SubscriptionCard account={data} />

            <StaffInvoicesCard accountId={data.id} onDone={done} />

            <PoliceFundingCard accountId={data.id} canWrite={staff.can('billing_plan_quota')} onResolve={setResolving} />

            <Card>
              <CardHeader>
                <CardTitle>Limits</CardTitle>
                <CardDescription>In force now. An override takes precedence over the plan.</CardDescription>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 lg:grid-cols-4" data-testid="staff-limits">
                  {QUOTA_FIELDS.map((f) => (
                    <Fact key={f.key} label={f.label}>
                      <span className="tabular-nums">{data.limits[f.key]}</span>{' '}
                      {data.overrides[f.key] === null ? null : <Badge variant="default">override</Badge>}
                    </Fact>
                  ))}
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Members</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Verified</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.members.map((m) => (
                      <TableRow key={m.user_id} data-testid="staff-member-row">
                        <TableCell className="max-w-[18rem] truncate font-mono text-xs"><StaffLink to={`/app/staff/users/${m.user_id}`}>{m.email}</StaffLink></TableCell>
                        <TableCell>{m.name ?? '—'}</TableCell>
                        <TableCell><Badge variant="secondary">{m.role}</Badge></TableCell>
                        <TableCell><StateBadge state={m.user_state} /></TableCell>
                        <TableCell className="text-xs">{m.email_verified ? 'yes' : 'no'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Zones</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead className="text-right">Connected</TableHead>
                      <TableHead className="text-right">Identities</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.zones.map((z) => (
                      <TableRow key={z.id} data-testid="staff-zone-row">
                        <TableCell className="max-w-[16rem] truncate"><StaffLink to={`/app/staff/zones/${z.id}`}>{z.name}</StaffLink></TableCell>
                        <TableCell><StateBadge state={z.zone_state} /></TableCell>
                        <TableCell className="text-right tabular-nums">{z.connected_count}</TableCell>
                        <TableCell className="text-right tabular-nums">{z.identity_count}</TableCell>
                        <TableCell className="text-right tabular-nums">{z.pending_message_count}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatAbsolute(z.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </PageBody>

      {resolving === null ? null : (
        <ResolveRefundDialog
          refund={resolving}
          onClose={() => { setResolving(null) }}
          onDone={(notified) => { done('Refund marked as paid out', notified) }}
        />
      )}

      {data === undefined || dialog === null ? null : dialog === 'plan' ? (
        <PlanDialog account={data} onClose={() => { setDialog(null) }} onDone={done} />
      ) : dialog === 'quotas' ? (
        <QuotaDialog account={data} onClose={() => { setDialog(null) }} onDone={done} />
      ) : dialog === 'balance' ? (
        <BalanceDialog account={data} onClose={() => { setDialog(null) }} onDone={done} />
      ) : dialog === 'view-as' ? (
        <ReasonDialog
          title={`View ${data.name} as staff`}
          description="Opens the account’s normal console pages read-only for up to an hour. A red banner stays on every page, and every start and end is audited. Message bodies stay hidden unless you also take break-glass on a zone."
          confirmLabel="Start viewing"
          onClose={() => { setDialog(null) }}
          onConfirm={async (reason) => {
            await beginViewAs(client, { id: data.id, name: data.name }, reason, staff.tier)
            void navigate('/app')
          }}
        />
      ) : (
        <ReasonDialog
          title={dialog === 'suspend' ? `Suspend ${data.name}?` : `Reactivate ${data.name}?`}
          description={
            dialog === 'suspend'
              ? 'Every zone of the account becomes unreachable and its agents disconnect. Their administrators are emailed with your reason.'
              : 'The account and its zones become reachable again. Their administrators are emailed with your reason.'
          }
          confirmLabel={dialog === 'suspend' ? 'Suspend account' : 'Reactivate account'}
          destructive={dialog === 'suspend'}
          onClose={() => { setDialog(null) }}
          onConfirm={async (reason) => {
            const r = await act.mutateAsync({ action: { kind: dialog === 'suspend' ? 'account-suspend' : 'account-reactivate', id: data.id }, reason })
            done(dialog === 'suspend' ? 'Account suspended' : 'Account reactivated', r.notified)
          }}
        />
      )}
    </StaffGate>
  )
}
